import {
  evaluateVisibility,
  hasAnswerContent,
  type QuestionResponse,
  type RecordResponseRequest,
  type SectionSkipLogic,
  type SkipAnswer,
  type SkipTriggerAnswer,
  type SubSection,
} from '@assessments/shared'
import { toRecordRequest } from './questionMapping.js'

/** The body the server stores for a cleared answer. */
export const CLEARED_REQUEST: RecordResponseRequest = { response: null, selectedOptionIds: [] }

function toSkipAnswer(response: QuestionResponse): SkipAnswer | null {
  const request = toRecordRequest(response)
  if (!hasAnswerContent(request.response, request.selectedOptionIds.length)) return null
  return {
    selectedOptionIds: request.selectedOptionIds,
    text: response.kind === 'text' ? response.text : null,
  }
}

/**
 * The answers on screen: saved answers from other sections, overlaid with this
 * section's live (possibly unsaved) answers.
 */
export function liveAnswers(
  triggerAnswers: readonly SkipTriggerAnswer[],
  subSections: readonly SubSection[],
  responses: Record<string, QuestionResponse>,
): Map<string, SkipAnswer> {
  const answers = new Map<string, SkipAnswer>(
    triggerAnswers.map(({ questionId, ...answer }) => [questionId, answer]),
  )
  for (const sub of subSections) {
    for (const question of sub.questions) {
      const response = responses[question.id]
      if (!response) continue
      const answer = toSkipAnswer(response)
      if (answer) answers.set(question.id, answer)
      else answers.delete(question.id)
    }
  }
  return answers
}

/**
 * Questions the skip rules hide for the answers on screen. `skipLogic` is absent from
 * an API that predates skip logic.
 */
export function deriveHiddenQuestionIds(
  skipLogic: SectionSkipLogic | undefined,
  subSections: readonly SubSection[],
  responses: Record<string, QuestionResponse>,
): Set<string> {
  if (!skipLogic || skipLogic.rules.length === 0) return new Set()
  return evaluateVisibility(
    skipLogic.rules,
    liveAnswers(skipLogic.triggerAnswers, subSections, responses),
  )
}

export function hasAnswer(response: QuestionResponse | undefined): boolean {
  return response !== undefined && toSkipAnswer(response) !== null
}

export function emptyResponse(response: QuestionResponse): QuestionResponse {
  switch (response.kind) {
    case 'single':
      return { kind: 'single', selectedOptionId: null }
    case 'multi':
      return { kind: 'multi', selectedOptionIds: [] }
    case 'text':
      return { kind: 'text', text: '' }
    case 'industry':
      return { kind: 'industry', subIndustryId: null }
  }
}

/** Answers for visible questions only, so section exits never write a hidden one. */
export function withoutHidden(
  responses: Record<string, QuestionResponse>,
  hidden: ReadonlySet<string>,
): Record<string, QuestionResponse> {
  return Object.fromEntries(Object.entries(responses).filter(([id]) => !hidden.has(id)))
}
